/*
 * EGR on Shopify — cart and links. Runs after egr-core.js and before egr-shell.js.
 *
 * 1. EGR.bag is the real Shopify cart (Ajax Cart API: /cart.js, /cart/add.js,
 *    /cart/change.js). Totals and discounts are Shopify's own numbers, so the
 *    drawer always matches checkout.
 * 2. EGR.url() maps the site's internal page names (store.html, product.html?p=…)
 *    to Shopify URLs, and every link rendered on the page is rewritten the same way.
 * 3. The /cart page.
 */
(function () {
  "use strict";
  const EGR = window.EGR;
  const S = window.EGR_SHOP || {};
  const C = EGR.catalog;
  const { $, esc, money, round2 } = EGR.u;
  const R = S.routes || {};
  const root = (R.root || "/").replace(/\/?$/, "/");
  const PAGE = (handle) => root + "pages/" + handle;

  /* ------------------------------------------------------------- routes */
  const POL = S.policies || {};
  const PAGES = {
    "coa.html": PAGE("coa"), "lot.html": PAGE("certificate"), "quality.html": PAGE("quality"),
    "library.html": PAGE("library"), "faq.html": PAGE("faq"), "shipping.html": PAGE("shipping"),
    "contact.html": PAGE("contact"), "wholesale.html": PAGE("wholesale"), "affiliates.html": PAGE("partners"),
    "bulk.html": PAGE("wholesale")
  };
  const LEGAL = {
    terms: POL.terms || root + "policies/terms-of-service",
    privacy: POL.privacy || root + "policies/privacy-policy",
    returns: POL.refund || root + "policies/refund-policy",
    "shipping-policy": POL.shipping || root + "policies/shipping-policy",
    "research-use": PAGE("research-use"),
    disclaimer: PAGE("research-use") + "#disclaimer"
  };
  function url(href) {
    if (!href || /^(https?:|mailto:|tel:|#|javascript:|\/\/)/i.test(href) || href.charAt(0) === "/") return href;
    const hashAt = href.indexOf("#");
    const hash = hashAt >= 0 ? href.slice(hashAt) : "";
    const rest = hashAt >= 0 ? href.slice(0, hashAt) : href;
    const [path, query] = rest.split("?");
    const q = new URLSearchParams(query || "");
    const withQ = (base, params) => { const s = params.toString(); return base + (s ? "?" + s : "") + hash; };
    switch (path) {
      case "": case "index.html": return root + hash;
      case "store.html": return withQ(R.all || root + "collections/all", q);
      case "product.html": { const h = q.get("p"); q.delete("p"); return withQ(root + "products/" + encodeURIComponent(h || ""), q); }
      case "cart.html": return withQ(R.cart || root + "cart", q);
      case "checkout.html": return root + "checkout";
      case "account.html": return R.account || root + "account";
      case "box.html": return R.all || root + "collections/all";
      case "legal.html": return LEGAL[q.get("doc") || "terms"] || LEGAL.terms;
      default: return PAGES[path] ? withQ(PAGES[path], q) : href;
    }
  }
  EGR.url = url;

  // Links to a store policy that hasn't been written yet (Settings > Policies) are
  // hidden rather than left pointing at a missing page.
  const POLICY = { terms: ["terms", "terms-of-service"], privacy: ["privacy", "privacy-policy"], returns: ["refund", "refund-policy"], "shipping-policy": ["shipping", "shipping-policy"] };
  function policyMissing(href) {
    if (!S.policies) return false;
    let doc = null;
    if (/^legal\.html/.test(href)) doc = new URLSearchParams(href.split("?")[1] || "").get("doc") || "terms";
    else { const m = /\/policies\/([\w-]+)/.exec(href); if (m) doc = Object.keys(POLICY).find((k) => POLICY[k][1] === m[1]); }
    return !!doc && !!POLICY[doc] && !POL[POLICY[doc][0]];
  }

  // Rewrite links as they are rendered (the shell and pages build HTML in JS).
  function fix(rootEl) {
    if (!rootEl || !rootEl.querySelectorAll) return;
    const list = rootEl.matches && rootEl.matches("a[href]") ? [rootEl] : [];
    rootEl.querySelectorAll("a[href]").forEach((a) => list.push(a));
    list.forEach((a) => {
      const h = a.getAttribute("href");
      if (policyMissing(h)) { a.hidden = true; return; }
      const u = url(h);
      if (u !== h) a.setAttribute("href", u);
    });
    if (rootEl.querySelectorAll) rootEl.querySelectorAll("[data-href]").forEach((el) => { const u = url(el.dataset.href); if (u !== el.dataset.href) el.dataset.href = u; });
  }
  EGR.fixLinks = fix;
  new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => { if (n.nodeType === 1) fix(n); }))).observe(document.documentElement, { childList: true, subtree: true });
  document.addEventListener("DOMContentLoaded", () => {
    fix(document.body);
    // Sections about lot lookup stay hidden until lots are published (assets/egr-lots.js).
    if (EGR.data.products.some((p) => p.variants.some((v) => v.lots && v.lots.length))) {
      document.querySelectorAll("[data-needs-lots]").forEach((el) => { el.hidden = false; });
    } else {
      // Until then, links and copy that point at certificates say something true instead.
      document.querySelectorAll("[data-nolots-href]").forEach((el) => { el.setAttribute("href", url(el.dataset.nolotsHref)); });
      document.querySelectorAll("[data-nolots-text]").forEach((el) => { el.textContent = el.dataset.nolotsText; });
    }
  });

  /* --------------------------------------------------------------- cart */
  let cart = S.cart || { items: [], item_count: 0, total_price: 0, original_total_price: 0, total_discount: 0, cart_level_discount_applications: [] };
  const listeners = [];
  const notify = () => listeners.forEach((fn) => { try { fn(); } catch (e) { console.error(e); } });
  async function api(path, body) {
    const r = await fetch(root + path, {
      method: body ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: body ? JSON.stringify(body) : undefined,
      credentials: "same-origin"
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.description || j.message || "Something went wrong. Please try again.");
    return j;
  }
  const set = (c) => { if (c && Array.isArray(c.items)) { cart = c; notify(); } return cart; };
  const refresh = () => api("cart.js").then(set);
  const fail = (err) => { if (EGR.toast) EGR.toast(err.message); return refresh().catch(() => cart); };

  // A cart line as the shell expects it, from a Shopify line item.
  function line(it) {
    const ref = C.variant(String(it.variant_id));
    const product = ref ? ref.product : {
      handle: it.handle, title: it.product_title, short: it.product_title, subtitle: "", tone: "sky", kind: "vial",
      spec: { form: "" }, variants: [], badges: [], image: it.image || null
    };
    const variant = ref ? ref.variant : { id: String(it.variant_id), title: it.variant_title || "", price: it.price / 100, lots: [], image: it.image || null };
    const retail = it.original_line_price / 100;
    const total = it.final_line_price / 100;
    const labels = (it.line_level_discount_allocations || []).map((d) => d.discount_application && d.discount_application.title).filter(Boolean);
    return {
      t: "item", v: String(it.variant_id), q: it.quantity, key: it.key, product, variant,
      lineTotal: total,
      pricing: { retail, total, discount: round2(retail - total), label: labels.join(", "), pct: 0, group: "" }
    };
  }

  EGR.bag = {
    lines: () => cart.items.map((it) => ({ t: "item", v: String(it.variant_id), q: it.quantity, key: it.key })),
    count: () => cart.item_count || 0,
    add(variantId, qty) {
      return api("cart/add.js", { items: [{ id: Number(variantId), quantity: Math.max(1, parseInt(qty, 10) || 1) }] }).then(refresh).catch(fail);
    },
    setQty(key, qty) {
      return api("cart/change.js", { id: key, quantity: Math.max(0, Math.min(999, parseInt(qty, 10) || 0)) }).then(set).catch(fail);
    },
    remove(key) { return this.setQty(key, 0); },
    clear() { return api("cart/clear.js", {}).then(set).catch(fail); },
    addBox() { if (EGR.toast) EGR.toast("Monthly boxes aren't available yet."); },
    totals() {
      const lines = cart.items.map(line);
      const savings = (cart.cart_level_discount_applications || []).map((d) => ({ label: d.title, amount: (d.total_allocated_amount || 0) / 100 }));
      const lineSaved = lines.reduce((s, l) => s + l.pricing.discount, 0);
      if (lineSaved > 0) savings.unshift({ label: "Discounts", amount: round2(lineSaved) });
      const total = cart.total_price / 100;
      const threshold = EGR.data.promo.freeShippingOver || 0;
      return {
        lines, savings, count: cart.item_count,
        retail: (cart.original_total_price || cart.total_price) / 100,
        total, discount: round2(((cart.original_total_price || 0) - cart.total_price) / 100),
        freeShipping: threshold > 0 && total >= threshold,
        toFree: threshold > 0 ? round2(Math.max(0, threshold - total)) : 0
      };
    },
    on(fn) { listeners.push(fn); },
    refresh
  };
  // Another tab changed the cart: pick it up when this one is shown again.
  document.addEventListener("visibilitychange", () => { if (document.visibilityState === "visible") refresh().catch(() => {}); });
  window.addEventListener("pageshow", (e) => { if (e.persisted) refresh().catch(() => {}); });

  /* ---------------------------------------------------------- cart page */
  EGR.pages = EGR.pages || {};
  EGR.pages.cart = {
    title: "Cart · EGR",
    init() {
      const el = $("#cart-page");
      if (!el) return;
      function render() {
        const t = EGR.bag.totals();
        if (!t.lines.length) {
          el.innerHTML = `<div class="empty" style="padding:96px 12px">${EGR.icon("bag", "icon-lg")}<h3>Your cart is empty.</h3><p>Browse the catalog to add a compound.</p><div class="btn-row"><a class="btn" href="store.html">Shop compounds</a></div></div>`;
          return;
        }
        const P = EGR.data.promo;
        const meter = !P.freeShippingOver ? "" : t.freeShipping
          ? `<div class="ship-meter"><span class="text-pass">${EGR.icon("check", "icon")}</span> <strong>Free shipping</strong> on this order.</div>`
          : `<div class="ship-meter">Add <strong>${money(t.toFree)}</strong> more for free shipping.<div class="progress"><span style="width:${Math.min(100, (t.total / P.freeShippingOver) * 100)}%"></span></div></div>`;
        el.innerHTML = `<div class="cart-layout">
<div class="cart-lines panel">${meter}${t.lines.map(EGR.cartLineHtml).join("")}</div>
<aside class="cart-aside panel">
<h2 class="title">Order summary</h2>
<div class="sum" style="margin-top:12px">
${t.savings.length ? `<div><span>Subtotal</span><span>${money(t.retail)}</span></div>
${t.savings.map((s) => `<div><span>${esc(s.label)}</span><span class="neg">−${money(s.amount)}</span></div>`).join("")}
<div class="total"><span>Subtotal after discounts</span><span>${money(t.total)}</span></div>` : `<div class="total"><span>Subtotal</span><span>${money(t.total)}</span></div>`}
</div>
<p class="small muted" style="margin-top:10px">Shipping and taxes are calculated at checkout.</p>
<label class="check" style="margin-top:16px"><input type="checkbox" id="cart-ruo"><span class="box">${EGR.icon("check")}</span><span>I confirm this order is for in-vitro laboratory research only, not for human or veterinary use.</span></label>
<a class="btn btn-lg btn-block btn-arrow" id="cart-checkout" href="checkout.html" style="margin-top:16px" aria-disabled="true">Check out<span class="btn-badge">${EGR.icon("arrow")}</span></a>
<p class="form-note" id="cart-note" aria-live="polite"></p>
<a class="more" href="store.html" style="margin-top:6px">Continue shopping</a>
</aside></div>`;
        const ruo = $("#cart-ruo"), go = $("#cart-checkout"), note = $("#cart-note");
        const sync = () => { go.setAttribute("aria-disabled", String(!ruo.checked)); ruo.closest(".check").classList.toggle("is-checked", ruo.checked); };
        ruo.addEventListener("change", () => { sync(); note.textContent = ""; });
        go.addEventListener("click", (e) => { if (!ruo.checked) { e.preventDefault(); note.textContent = "Tick the research-use confirmation to continue."; ruo.focus(); } });
        sync();
      }
      render();
      EGR.bag.on(render);
    }
  };
})();
